import { registerBrick } from 'olympe';
import {ReactBrick, useMUITheme, updateNavigationState, parseCurrentHash} from "@olympeio/core";
import React, {useEffect, useRef} from "react";
import { Card, CardContent } from "@mui/material";
import { LineChart } from "lucide-react";
import { ThemeProvider } from '@mui/material/styles';
import Zoomist from "zoomist";


export default class AnalysisStrategyDetail extends ReactBrick {

    /**
     * @override
     */
    static getReactComponent($) {

        return () => {
            const theme = useMUITheme($);
            const isTrend = parseCurrentHash().includes('trend');
            const containerRefTrend = useRef(null);
            const containerRefRange = useRef(null);

            useEffect(() => {
                setTimeout(() => {
                    if (containerRefTrend.current) {
                        new Zoomist(containerRefTrend.current, {
                            zoomer: true, // enables zooming
                            wheel: true,  // enables scroll zoom
                            pinch: true,  // enables pinch to zoom
                            draggable: true,
                        });
                    }
                    if (containerRefRange.current) {
                        new Zoomist(containerRefRange.current, {
                            zoomer: true, // enables zooming
                            wheel: true,  // enables scroll zoom
                            pinch: true,  // enables pinch to zoom
                            draggable: true,
                        });
                    }
                }, 200);
            }, []);

            return (
                <ThemeProvider theme={theme}>
                    <div className="h-full w-full bg-indigo-50 flex flex-col items-center p-6 overflow-y-auto">
                        {/* Header */}
                        <header className="w-full max-w-4xl text-center py-6">
                            <h1 className="text-4xl font-bold text-indigo-700">{isTrend ? 'Stratégie Dynamique' : 'Stratégie Prudente'}</h1>
                        </header>

                        {/* Strategies Section */}
                        <section className="w-full max-w-4xl my-4">
                            {!isTrend && (
                                <Card className="m-4">
                                    <CardContent className="p-6">
                                        <h2 className="text-2xl font-semibold text-indigo-700">Une stratégie pensée pour le long terme, sur des cryptos solides</h2>
                                        <p className="text-gray-600 mt-4 text-justify">
                                            Cette méthode repose sur un principe simple : <b>acheter lorsque le prix baisse, et vendre lorsqu’il remonte</b>.
                                            On appelle ça une stratégie de "range". Elle est idéale pour les cryptomonnaies en lesquelles on croit sur le long terme, comme le Bitcoin, par exemple.
                                        </p>
                                        <div className="zoomist w-full max-w-full overflow-hidden my-8" ref={containerRefRange}>
                                            <div className="zoomist-wrapper">
                                                <img src="/images/range.png" alt="Explication range" className="zoomist-image w-full h-auto" />
                                            </div>
                                        </div>
                                        <h3 className="text-xl font-semibold text-indigo-700">Pourquoi c’est rassurant ?</h3>
                                        <p className="text-gray-600 mt-4 text-justify">
                                            Avec cette stratégie, <b>aucune vente n’est faite à perte</b>. On ne vend que lorsque le prix est reparti à la hausse,
                                            ce qui veut dire que chaque transaction se conclut avec un gain.
                                            Cela peut parfois demander un peu de patience, mais c’est justement ce qui en fait une stratégie adaptée à une vision long terme.
                                        </p>
                                        <h3 className="text-xl font-semibold mt-8 text-indigo-700">Ce qu’il faut savoir</h3>
                                        <p className="text-gray-600 mt-4 text-justify">
                                            Pour être efficace, <b>cette approche répartit votre capital en petites portions</b>. Cela permet d’acheter progressivement si les prix continuent de baisser,
                                            sans tout miser d’un coup. En revanche, cela signifie qu’il faut disposer d’un capital plus conséquent pour que la stratégie reste active, même en cas de forte baisse du marché.
                                        </p>
                                        <p className="text-gray-600 mt-4 text-justify">
                                            Autre point important : le retrait des fonds. <b>Il est préférable d’attendre des périodes où le marché est au plus haut (ce qu’on appelle un “ATH” ou All Time High) pour récupérer ses gains</b>.
                                            Retirer ses actifs en dehors de ces périodes peut signifier vendre certaines positions à perte, ce qui va à l’encontre de la logique de cette stratégie.
                                        </p>
                                        <br /><br />
                                        <div className="flex items-center justify-center text-indigo-700 cursor-pointer text-center" onClick={() => updateNavigationState('/analysis/range')}>
                                            <LineChart className="w-6 h-6 mr-2" />
                                            <span className="font-semibold">Retour au simulateur</span>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                            {isTrend && (
                                <Card className="m-4">
                                    <CardContent className="p-6">
                                        <h2 className="text-2xl font-semibold text-indigo-700">Une stratégie simple et efficace pour surfer sur la tendance</h2>
                                        <p className="text-gray-600 mt-4 text-justify">
                                            Notre méthode repose sur un principe clair : <b>acheter quand le marché est en hausse, et vendre quand il commence à redescendre</b>.
                                            L'idée ? Profiter des moments où le prix d'une cryptomonnaie grimpe, tout en évitant d'investir quand il chute.
                                            C’est une façon de maximiser les gains tout en limitant les risques.
                                        </p>
                                        <div className="zoomist w-full max-w-full overflow-hidden my-8" ref={containerRefTrend}>
                                            <div className="zoomist-wrapper">
                                                <img src="/images/trend.png" alt="Explication trend" className="zoomist-image w-full h-auto" />
                                            </div>
                                        </div>
                                        <h3 className="text-xl font-semibold text-indigo-700">Pourquoi ça marche ?</h3>
                                        <p className="text-gray-600 mt-4 text-justify">
                                            Cette stratégie ne cherche pas à être active en permanence. Au contraire, <b>elle sait rester à l’écart quand les conditions ne sont pas favorables</b>,
                                            ce qui vous laisse la liberté de récupérer votre investissement à tout moment. Et même quand un trade est en cours, il est souvent possible de sortir
                                            avec un bénéfice, puisque l’achat a été déclenché pendant une période de hausse.
                                        </p>
                                        <h3 className="text-xl font-semibold mt-8 text-indigo-700">À garder en tête</h3>
                                        <p className="text-gray-600 mt-4 text-justify">
                                            Comme toute stratégie, celle-ci a aussi ses limites. Elle peut être <b>sensible aux fortes variations de prix</b> (la volatilité).
                                            Chaque opération utilise 100 % du capital prévu, sans le répartir. Si le marché baisse brutalement, la stratégie peut entraîner des pertes.
                                            <b>Toutefois, elle est conçue pour réagir rapidement et limiter ces pertes au maximum</b>.
                                        </p>
                                        <br /><br />
                                        <div className="flex items-center justify-center text-indigo-700 cursor-pointer text-center" onClick={() => updateNavigationState('/analysis/trend')}>
                                            <LineChart className="w-6 h-6 mr-2" />
                                            <span className="font-semibold">Retour au simulateur</span>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </section>

                        {/* Disclaimer */}
                        <footer className="w-full max-w-4xl mt-12 p-4 border-t border-gray-300 text-center text-sm text-gray-500">
                            <p>
                                Avertissement : Le trading comporte des risques de pertes. Aucune performance passée ne garantit des résultats futurs.
                                Investissez uniquement ce que vous pouvez vous permettre de perdre.
                            </p>
                        </footer>
                    </div>
                </ThemeProvider>
            );
        }
    }
}

registerBrick('01961949995bd4075ab7', AnalysisStrategyDetail);
